import { useCallback, useMemo } from 'react';

import { useListSubCatalogsQuery } from '../../../../../generated';

interface SubCatalog {
  id: string;
  name: string;
  supplierEntityId: string;
}

interface UseListSubCatalogsReturn {
  subcatalogList: SubCatalog[];
  loading: boolean;
  error?: Error;
  fetchAll: () => Promise<void>;
}

export const useListSubCatalogs = (
  supplierId?: string
): UseListSubCatalogsReturn => {
  const { data, loading, error, refetch } = useListSubCatalogsQuery({
    variables: {
      supplierEntityId: supplierId,
      first: 100, // Get more items at once to avoid pagination
    },
    skip: !supplierId,
  });

  const subcatalogList = useMemo(() => {
    const connection = data?.listSubCatalogs;
    if (!connection || connection.__typename === 'ValidationError') return [];

    if ('edges' in connection) {
      const { edges } = connection;
      if (!edges?.length) return [];

      return edges.map(
        (edge): SubCatalog => ({
          id: edge.node.id,
          name: edge.node.name,
          supplierEntityId: edge.node.supplierEntityId,
        })
      );
    }

    return [];
  }, [data]);

  const fetchAll = useCallback(async () => {
    if (!supplierId) return;
    await refetch();
  }, [supplierId, refetch]);

  return {
    subcatalogList,
    loading,
    error,
    fetchAll,
  };
};
